import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EffectThemeContainer from "../../effectTheme/effectThemeContainer";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { createPortal } from "react-dom";
import { InfoPopoverButtonProps } from "../../../types/imageStudio/info";

export const InfoPopoverButton = ({
    children,
    width = 280,
    maxHeight = 500,
    image,
    imageHeight,
    imageWidth,
    imageBorder = true,
    placement = "right",
    containerClass = "p-2"
}: InfoPopoverButtonProps) => {
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);

    const [visible, setVisible] = useState(false);
    const [locked, setLocked] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const updatePosition = () => {
        const button = buttonRef.current;
        if (!button) return;

        const rect = button.getBoundingClientRect();
        const gap = 8;
        const padding = 8;

        let nextTop = rect.bottom + gap;

        let nextLeft =
            placement === "right"
                ? rect.right + gap
                : rect.left - width - gap;

        nextLeft = Math.max(
            padding,
            Math.min(nextLeft, window.innerWidth - width - padding)
        );

        const popoverHeight = Math.min(
            popoverRef.current?.offsetHeight ?? 0,
            maxHeight
        );

        if (popoverHeight > 0) {
            const bottom = nextTop + popoverHeight;

            if (bottom > window.innerHeight - padding) {
                nextTop = window.innerHeight - popoverHeight - padding;
            }

            if (nextTop < padding) {
                nextTop = padding;
            }
        }

        setPosition({
            top: nextTop,
            left: nextLeft
        });
    };

    useLayoutEffect(() => {
        if (!visible) return;

        updatePosition();

        requestAnimationFrame(() => {
            updatePosition();
        });
    }, [visible, placement, width, children]);

    useEffect(() => {
        if (!visible) return;

        const handleResizeOrScroll = () => {
            updatePosition();
        };

        window.addEventListener("resize", handleResizeOrScroll);
        window.addEventListener("scroll", handleResizeOrScroll, true);

        return () => {
            window.removeEventListener("resize", handleResizeOrScroll);
            window.removeEventListener("scroll", handleResizeOrScroll, true);
        };
    }, [visible, placement, width]);

    useEffect(() => {
        if (!locked) return;

        const handleMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;

            if (buttonRef.current?.contains(target)) return;
            if (popoverRef.current?.contains(target)) return;

            setLocked(false);
            setVisible(false);
        };

        window.addEventListener("mousedown", handleMouseDown);

        return () => {
            window.removeEventListener("mousedown", handleMouseDown);
        };
    }, [locked]);

    const buttonContent =
        typeof image === "string" ? (
            <img
                src={image}
                alt=""
                style={{
                    width: imageWidth ?? imageHeight ?? 22,
                    height: imageHeight ?? imageWidth ?? 22,
                    objectFit: "cover",
                }}
            />
        ) : image ? (
            image
        ) : (
            <FontAwesomeIcon icon={faInfo} size="xs" />
        );

    const popover =
        visible
            ? createPortal(
                <div
                    ref={popoverRef}
                    style={{
                        position: "fixed",
                        top: position.top,
                        left: position.left,
                        width,
                        transform: "none",
                        zIndex: 22000,
                        pointerEvents: "auto"
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <EffectThemeContainer
                        containerClass={containerClass}
                        children={
                            <div
                                style={{
                                    maxHeight,
                                    overflowY: "auto",
                                    overflowX: "hidden"
                                }}
                            >
                                {children}
                            </div>
                        }
                    />
                </div>,
                document.body
            )
            : null;

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={`btn bg-transparent p-0 d-inline-flex align-items-center justify-content-center ${imageBorder || !image ? "border border-secondary rounded-circle" : ""}`}
                style={{
                    width: image ? imageHeight : 22,
                    height: image ? imageHeight : 22,
                    minWidth: image ? imageHeight : 22,
                    lineHeight: 1,
                    overflow: "hidden",
                    verticalAlign: "middle"
                }}
                onMouseEnter={() => {
                    if (locked) return;
                    updatePosition();
                    setVisible(true);
                }}
                onMouseLeave={() => {
                    if (locked) return;
                    setVisible(false);
                }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    updatePosition();

                    setLocked((prev) => {
                        const next = !prev;
                        setVisible(next);
                        return next;
                    });
                }}
            >
                {buttonContent}
            </button>

            {popover}
        </>
    );
};